import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Navigation, Phone, MessageCircle, Clock, Loader2, Truck, Store, Home, CheckCircle, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/services/api';
import useAnimatedPosition from '@/hooks/useAnimatedPosition';

// Ícone da moto rotacionável (SVG natural aponta para a direita = 90°).
const buildIconeMoto = (rotationDeg = 0) => L.divIcon({
  className: 'custom-bike-icon',
  html: `<div style="background:#ea580c;border-radius:50%;width:48px;height:48px;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(234,88,12,0.5);border:3px solid white;">
    <span style="display:flex;transform:rotate(${rotationDeg}deg);transition:transform 1s ease-out;">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="white"><path d="M19 12c-1.49 0-2.87.47-4 1.27V11l4-4h-3V5h-2v2h-3l-2 2H7c-1.66 0-3 1.34-3 3v3l5 1v3l3-3-2.59-2.59c-.78-.78-.78-2.05 0-2.83.78-.78 2.05-.78 2.83 0L14 11c.93-1.21 2.39-2 4-2 2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5h2c0 1.66 1.34 3 3 3s3-1.34 3-3-1.34-3-3-3z"/></svg>
    </span>
  </div>`,
  iconSize: [48, 48],
  iconAnchor: [24, 24],
});
const iconeOrigem = L.divIcon({
  html: `<div style="background:#16a34a;border-radius:50% 50% 50% 0;width:36px;height:36px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid white;"><svg width="18" height="18" viewBox="0 0 24 24" fill="white" style="transform:rotate(45deg);"><path d="M12 2L2 12h3v8h6v-6h2v6h6v-8h3L12 2z"/></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});
const iconeDestino = L.divIcon({
  html: `<div style="background:#dc2626;border-radius:50% 50% 50% 0;width:36px;height:36px;transform:rotate(-45deg);display:flex;align-items:center;justify-content:center;border:2px solid white;"><svg width="18" height="18" viewBox="0 0 24 24" fill="white" style="transform:rotate(45deg);"><path d="M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z"/></svg></div>`,
  iconSize: [36, 36],
  iconAnchor: [18, 36],
});

const FitBounds = ({ pontos }) => {
  const map = useMap();
  useEffect(() => {
    const validos = pontos.filter(p => p && p[0] && p[1]);
    if (validos.length === 0) return;
    if (validos.length === 1) map.setView(validos[0], 15);
    else map.fitBounds(validos, { padding: [50, 50] });
  }, [pontos, map]);
  return null;
};

const TrackingPublico = () => {
  const { token } = useParams();
  const [tracking, setTracking] = useState(null);
  const [mapConfig, setMapConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const pollingRef = useRef(null);
  
  // Posição alvo do entregador (deriva de tracking, antes de qualquer early return — Rules of Hooks)
  const entregadorPosAlvo = tracking?.entregador_posicao
    ? [tracking.entregador_posicao.latitude, tracking.entregador_posicao.longitude]
    : null;
  const { position: entregadorPosAnimada, bearing } = useAnimatedPosition(entregadorPosAlvo, 1500);
  const iconeMoto = useMemo(() => buildIconeMoto(bearing - 90), [bearing]);
  
  useEffect(() => {
    api.get('/maps/config').then(r => setMapConfig(r.data)).catch(() => {});
  }, []);
  
  useEffect(() => {
    if (!token) return;
    let mounted = true;
    const carregar = async () => {
      try {
        const r = await api.get(`/tracking/public/${token}`);
        if (mounted) {
          setTracking(r.data);
          setLoading(false);
        }
      } catch (e) {
        if (mounted) {
          setErro(e.response?.data?.detail || 'Tracking não encontrado');
          setLoading(false);
        }
      }
    };
    carregar();
    pollingRef.current = setInterval(carregar, 10000); // polling 10s para público
    return () => { mounted = false; if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [token]);
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <Loader2 className="w-10 h-10 text-orange-600 animate-spin" />
      </div>
    );
  }
  
  if (erro || !tracking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="bg-white rounded-2xl p-8 text-center max-w-md">
          <X className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="font-bold text-slate-900">{erro || 'Tracking indisponível'}</p>
        </div>
      </div>
    );
  }
  
  const { entrega, entregador, entregador_posicao, rota } = tracking;
  const origem = entrega?.origem_latitude && entrega?.origem_longitude ? [entrega.origem_latitude, entrega.origem_longitude] : null;
  const destino = entrega?.destino_latitude && entrega?.destino_longitude ? [entrega.destino_latitude, entrega.destino_longitude] : null;
  const entregadorPos = entregador_posicao ? [entregador_posicao.latitude, entregador_posicao.longitude] : null;
  const pontos = [origem, destino, entregadorPos].filter(Boolean);
  
  const tileUrl = mapConfig?.geoapify_key
    ? `https://maps.geoapify.com/v1/tile/osm-bright/{z}/{x}/{y}.png?apiKey=${mapConfig.geoapify_key}`
    : 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
  const tileAttribution = mapConfig?.geoapify_key
    ? '© Geoapify © OpenStreetMap contributors'
    : '© OpenStreetMap contributors';
  
  const statusLabel = {
    'criada': { text: 'Aguardando entregador', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    'aceita': { text: 'Entregador aceitou', color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
    'atribuida': { text: 'Entregador a caminho', color: 'bg-blue-100 text-blue-800', icon: Truck },
    'em_rota': { text: 'Em rota com o pedido', color: 'bg-orange-100 text-orange-800', icon: Navigation },
    'entregue': { text: 'Entregue!', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  }[entrega?.status] || { text: entrega?.status || '', color: 'bg-slate-100', icon: Clock };
  const StatusIcon = statusLabel.icon;
  
  const tel = (entregador?.telefone || '').replace(/\D/g, '');
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Header simples (sem login) */}
      <header className="bg-white border-b shadow-sm px-4 py-3 flex items-center gap-3">
        <img src="/logo-rota017.png" alt="Rota 017" className="w-10 h-10 rounded-xl object-cover" />
        <div>
          <h1 className="font-bold text-slate-900">Rota 017</h1>
          <p className="text-xs text-slate-500">Acompanhamento de entrega</p>
        </div>
      </header>
      
      <div className="flex-1 grid md:grid-cols-3">
        <div className="md:col-span-2 relative bg-slate-100 min-h-[400px]">
          <MapContainer center={pontos[0] || [-23.5505, -46.6333]} zoom={13} style={{ height: '100%', width: '100%', minHeight: '400px' }}>
            <TileLayer url={tileUrl} attribution={tileAttribution} />
            {origem && <Marker position={origem} icon={iconeOrigem}><Popup>Origem</Popup></Marker>}
            {destino && <Marker position={destino} icon={iconeDestino}><Popup>Destino</Popup></Marker>}
            {entregadorPosAnimada && <Marker position={entregadorPosAnimada} icon={iconeMoto}><Popup>{entregador?.nome || 'Entregador'}</Popup></Marker>}
            {rota?.polyline && rota.polyline.length > 1 && (
              <Polyline positions={rota.polyline} pathOptions={{ color: '#ea580c', weight: 5, opacity: 0.8 }} />
            )}
            <FitBounds pontos={pontos} />
          </MapContainer>
        </div>
        <div className="p-4 space-y-4 bg-white">
          <div className={`rounded-xl p-3 flex items-center gap-3 ${statusLabel.color}`}>
            <StatusIcon className="w-5 h-5" />
            <p className="font-semibold text-sm">{statusLabel.text}</p>
          </div>
          {rota && entrega?.status === 'em_rota' && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-3">
              <p className="text-xs text-orange-700">Tempo estimado</p>
              <p className="text-3xl font-bold text-orange-800">{rota.duracao_min} min</p>
              <p className="text-xs text-orange-600">{rota.distancia_km.toFixed(1)} km</p>
            </div>
          )}
          {entregador && (
            <div className="bg-slate-50 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                  <Truck className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="font-semibold">{entregador.nome}</p>
                  <p className="text-xs text-slate-500">{entregador.razao_social}</p>
                </div>
              </div>
              {tel && (
                <div className="grid grid-cols-2 gap-2">
                  <a href={`tel:+${tel}`} className="flex items-center justify-center gap-1 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-sm font-medium">
                    <Phone className="w-4 h-4" /> Ligar
                  </a>
                  <a href={`https://wa.me/${tel}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1 py-2 rounded-lg bg-green-500 hover:bg-green-600 text-white text-sm font-medium">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                </div>
              )}
            </div>
          )}
          <div className="space-y-2 text-sm">
            {entrega?.origem_endereco && (
              <div className="flex items-start gap-2"><Store className="w-4 h-4 text-green-600 mt-0.5" /><div><p className="text-xs text-slate-500">Origem</p><p>{entrega.origem_endereco}</p></div></div>
            )}
            {entrega?.destino_endereco && (
              <div className="flex items-start gap-2"><Home className="w-4 h-4 text-red-600 mt-0.5" /><div><p className="text-xs text-slate-500">Destino</p><p>{entrega.destino_endereco}</p></div></div>
            )}
          </div>
          {entregador_posicao?.atualizado_em && (
            <p className="text-xs text-slate-400 text-center pt-2 border-t">
              Atualizado: {new Date(entregador_posicao.atualizado_em).toLocaleTimeString('pt-BR')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TrackingPublico;
